import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Plus, Loader2, Pencil, Trash2, X, Check, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Investment types that support dividends/distributions
const ELIGIBLE_TYPES = new Set(['quoted_stocks', 'etf', 'mutual_fund', 'private_equity']);

const LABEL_FOR = {
  quoted_stocks: { singular: 'Dividend', plural: 'Dividends', section: 'Dividends' },
  etf: { singular: 'Distribution', plural: 'Distributions', section: 'Distributions' },
  mutual_fund: { singular: 'Distribution', plural: 'Distributions', section: 'Distributions' },
  private_equity: { singular: 'Distribution', plural: 'Distributions', section: 'Distributions' },
};

function fmtMoney(v, cur) {
  if (v == null || v === '') return '';
  try {
    return `${cur ? cur + ' ' : ''}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `${v}`;
  }
}

const emptyForm = (defaultCurrency) => ({
  received_date: new Date().toISOString().slice(0, 10),
  gross_amount: '',
  currency: defaultCurrency || '',
  withholding_tax: '',
  notes: '',
  external_reference: '',
});

/**
 * Collapsible Dividends / Distributions section for the edit-investment modal.
 *
 * Only renders for eligible investment types. Persists to /api/investments/{id}/dividends.
 */
export default function DividendsSection({ investment }) {
  const eligible = investment && ELIGIBLE_TYPES.has(investment.type);
  const labels = LABEL_FOR[investment?.type] || LABEL_FOR.quoted_stocks;
  const investmentId = investment?.investment_id;
  const defaultCurrency = investment?.currency || '';

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm(defaultCurrency));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchEvents = async () => {
    if (!eligible || !investmentId) return;
    setLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/dividends`, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`Failed to load (${resp.status})`);
      const data = await resp.json();
      setEvents(data.events || []);
    } catch (e) {
      toast.error(e.message || `Failed to load ${labels.plural.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load events the first time the section is expanded
  useEffect(() => {
    if (open && events.length === 0 && !loading) {
      fetchEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetForm = () => {
    setForm(emptyForm(defaultCurrency));
    setEditingId(null);
    setAddOpen(false);
  };

  const startEdit = (ev) => {
    setEditingId(ev.event_id);
    setForm({
      received_date: ev.received_date || '',
      gross_amount: ev.gross_amount ?? '',
      currency: ev.currency || defaultCurrency,
      withholding_tax: ev.withholding_tax ?? '',
      notes: ev.notes || '',
      external_reference: ev.external_reference || '',
    });
    setAddOpen(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.received_date) { toast.error('Date is required'); return; }
    const gross = Number(form.gross_amount);
    if (!gross || gross <= 0) { toast.error('Gross amount must be > 0'); return; }

    const payload = {
      received_date: form.received_date,
      gross_amount: gross,
      currency: (form.currency || defaultCurrency || '').toUpperCase() || undefined,
    };
    if (form.withholding_tax !== '' && Number(form.withholding_tax) >= 0) {
      payload.withholding_tax = Number(form.withholding_tax);
    }
    if (form.notes) payload.notes = form.notes;
    if (form.external_reference) payload.external_reference = form.external_reference;

    setSaving(true);
    try {
      const url = editingId
        ? `${BACKEND_URL}/api/investments/${investmentId}/dividends/${editingId}`
        : `${BACKEND_URL}/api/investments/${investmentId}/dividends`;
      const method = editingId ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      toast.success(editingId ? `${labels.singular} updated` : `${labels.singular} recorded`);
      resetForm();
      await fetchEvents();
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete this ${labels.singular.toLowerCase()}? This can't be undone.`)) return;
    setDeletingId(ev.event_id);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/dividends/${ev.event_id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Delete failed (${resp.status})`);
      toast.success(`${labels.singular} removed`);
      setEvents((prev) => prev.filter((e) => e.event_id !== ev.event_id));
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  if (!eligible) return null;

  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50/50" data-testid="dividends-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-100/50 rounded-lg"
        data-testid="dividends-section-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
          <Receipt className="w-4 h-4 text-emerald-700" />
          <span className="text-sm font-semibold text-slate-800">{labels.section}</span>
          {events.length > 0 && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-xs">
              {events.length} recorded
            </Badge>
          )}
        </div>
        <span className="text-xs text-slate-500 hidden sm:inline">
          {open ? 'Hide' : 'Manage'} {labels.plural.toLowerCase()}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid="dividends-section-body">
          {loading ? (
            <div className="flex items-center text-sm text-slate-500 py-3">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : events.length === 0 && !addOpen ? (
            <p className="text-xs text-slate-500 py-2">
              No {labels.plural.toLowerCase()} recorded yet. They&apos;ll be included in your Tax Data Pack once added.
            </p>
          ) : (
            <div className="space-y-2" data-testid="dividends-section-list">
              {events.map((ev) => (
                <div
                  key={ev.event_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs"
                  data-testid={`dividend-row-${ev.event_id}`}
                >
                  <div className="flex flex-col">
                    <span className="text-slate-900">
                      <span className="font-medium">{ev.received_date}</span>{' · '}
                      <span className="font-medium">{fmtMoney(ev.gross_amount, ev.currency)}</span>
                      {ev.withholding_tax ? <span className="text-slate-500"> · WHT {fmtMoney(ev.withholding_tax, ev.currency)}</span> : null}
                    </span>
                    {ev.notes && <span className="text-slate-500 mt-0.5">{ev.notes}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => startEdit(ev)}
                      className="h-7 w-7 p-0 text-slate-500 hover:text-emerald-700"
                      data-testid={`dividend-edit-${ev.event_id}`}
                      aria-label="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => handleDelete(ev)}
                      disabled={deletingId === ev.event_id}
                      className="h-7 w-7 p-0 text-slate-500 hover:text-red-600"
                      data-testid={`dividend-delete-${ev.event_id}`}
                      aria-label="Delete"
                    >
                      {deletingId === ev.event_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {addOpen ? (
            <div className="rounded-md border border-slate-300 bg-white p-3 space-y-3" data-testid="dividend-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Received date</Label>
                  <Input
                    type="date" value={form.received_date}
                    onChange={(e) => setForm({ ...form, received_date: e.target.value })}
                    className="mt-1 h-9 text-sm"
                    data-testid="dividend-form-received-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Gross amount</Label>
                  <Input
                    type="number" step="0.01" min="0" placeholder="0.00"
                    value={form.gross_amount}
                    onChange={(e) => setForm({ ...form, gross_amount: e.target.value })}
                    className="mt-1 h-9 text-sm"
                    data-testid="dividend-form-gross-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Currency</Label>
                  <Input
                    type="text" maxLength={3}
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                    placeholder={defaultCurrency || 'USD'}
                    className="mt-1 h-9 text-sm uppercase"
                    data-testid="dividend-form-currency"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Withholding tax <span className="text-slate-400">(optional)</span></Label>
                  <Input
                    type="number" step="0.01" min="0" placeholder="0.00"
                    value={form.withholding_tax}
                    onChange={(e) => setForm({ ...form, withholding_tax: e.target.value })}
                    className="mt-1 h-9 text-sm"
                    data-testid="dividend-form-wht"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Notes <span className="text-slate-400">(optional)</span></Label>
                <Input
                  type="text" maxLength={500}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder='e.g., "Q2 2025 ordinary dividend"'
                  className="mt-1 h-9 text-sm"
                  data-testid="dividend-form-notes"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={resetForm} disabled={saving} data-testid="dividend-form-cancel">
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
                <Button type="button" size="sm" onClick={handleSave} disabled={saving}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid="dividend-form-save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  {editingId ? 'Save changes' : `Record ${labels.singular.toLowerCase()}`}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button" variant="outline" size="sm" onClick={() => setAddOpen(true)}
              className="text-emerald-800 border-emerald-300 hover:bg-emerald-50"
              data-testid="dividend-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Record a {labels.singular.toLowerCase()}
            </Button>
          )}

          <p className="text-[11px] text-slate-500 mt-2">
            Amounts are recorded in native currency. Conversion is your accountant&apos;s call — we surface raw data only.
          </p>
        </div>
      )}
    </div>
  );
}
